import { Canvas, useThree } from '@react-three/fiber';
import { Perf } from 'r3f-perf';
import {
  type PropsWithChildren,
  useEffect,
  useId,
  useMemo,
  useRef,
} from 'react';
import { debounceTime, filter, fromEvent, tap } from 'rxjs';

import { useSubscriptions } from '@/package';
import { Eye, EyeOff, VIconButton, useFlag } from '@/package';
import { useToolMenuStore, useToolSettingStore } from '@/store';

import { useToolPcdCanvasContext } from './ToolPcdCanvas';

type ToolCanvasProps = {
  enabledFullScreen?: boolean;
};

const InnerToolCanvas = ({
  getParentElement,
}: {
  getParentElement: () => HTMLElement;
}) => {
  const { gl } = useThree();
  const cameraPannelShow = useToolSettingStore(state => state.cameraPannelShow);
  const toolMap = useToolMenuStore(state => state.toolMap);
  const fullScreenTool = useMemo(
    () => useToolMenuStore.getState().findBy('name', 'fullScreen')!,
    [toolMap],
  );

  const onFullscreenChange = () => {
    const parent = getParentElement();

    if (!document.fullscreenElement && parent) {
      const { offsetHeight, offsetWidth } = parent;

      gl.domElement.style.display = 'none';

      gl.setSize(offsetWidth, offsetHeight);
      gl.domElement.style.display = '';
    }
  };

  useSubscriptions(() => [
    fromEvent<Event>(window, 'resize')
      .pipe(debounceTime(0), tap(onFullscreenChange))
      .subscribe(),

    fromEvent<Event>(window, 'fullscreenchange')
      .pipe(
        tap(() => window.dispatchEvent(new Event('resize'))),
        filter(() => document.fullscreenElement == null),
        tap(() => useToolMenuStore.getState().deselect(fullScreenTool.id)),
      )
      .subscribe(),
  ]);

  useEffect(() => {
    window.dispatchEvent(new Event('resize'));
  }, [cameraPannelShow]);

  return <></>;
};

const ToolCanvas = ({ children }: PropsWithChildren<ToolCanvasProps>) => {
  const toolMap = useToolMenuStore(state => state.toolMap);
  const { isViewMode } = useToolPcdCanvasContext();
  const id = useId();
  const fullScreenTool = useMemo(
    () => useToolMenuStore.getState().findBy('name', 'fullScreen')!,
    [toolMap],
  );
  const elRef = useRef<HTMLDivElement>(null);
  const {
    value: visiblePerf,
    setFalse: hidePerf,
    setTrue: showPerf,
  } = useFlag(false);

  useEffect(() => {
    if (fullScreenTool.isSelected) {
      elRef.current?.requestFullscreen();
    } else {
      document.fullscreenElement && document.exitFullscreen();
    }
  }, [fullScreenTool.isSelected]);
  return (
    <div ref={elRef} className="relative size-full overflow-hidden bg-black">
      {import.meta.env.MODE === 'development' && isViewMode.default && (
        <VIconButton
          Icon={visiblePerf ? EyeOff : Eye}
          className="absolute bottom-18.5 left-0 z-1"
          size="sm"
          variant="roundSecondary"
          onClick={visiblePerf ? hidePerf : showPerf}
        />
      )}

      <Canvas id={id}>
        {visiblePerf && (
          <Perf className="bottom-18.5! left-17! z-0!" position="bottom-left" />
        )}
        <InnerToolCanvas getParentElement={() => elRef.current!} />

        {children}
      </Canvas>
    </div>
  );
};

export default ToolCanvas;
